import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

const GAME_ROUTES = ['/lobby', '/game'];

const FooterContainer = styled.footer<{ $wide: boolean }>`
  position: fixed;
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '960px' : '600px')};
  box-sizing: border-box;
  height: 40px;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  background: rgb(13, 13, 13);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-left: 12px;
  z-index: 10;
  border-top: 1px solid rgb(13, 13, 13);

  /* Desktop: span the whole viewport instead of floating as a narrow strip */
  @media (min-width: 1024px) {
    max-width: 100%;
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const FooterText = styled.p`
  /* Figma specs for footer text */
  color: rgb(255, 255, 255);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 9px;
  letter-spacing: 0%;
  text-align: left;
  margin: 0;
`;

const RightSlot = styled.div`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SocialLink = styled.a`
  color: rgb(255, 255, 255);
  text-decoration: none;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover { opacity: 0.8; }
`;

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm5.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.9 3.5 12 3.5 12 3.5s-7.9 0-9.4.6A3 3 0 00.5 6.2 31.8 31.8 0 000 12a31.8 31.8 0 00.5 5.8 3 3 0 002.1 2.1c1.5.6 9.4.6 9.4.6s7.9 0 9.4-.6a3 3 0 002.1-2.1A31.8 31.8 0 0024 12a31.8 31.8 0 00-.5-5.8zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
  </svg>
);

// The app went live in 2024; the end year tracks the current year automatically
// so the copyright never goes stale again.
const COPYRIGHT_START = 2024;

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const isWide = !GAME_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
  const currentYear = new Date().getFullYear();
  const yearRange =
    currentYear > COPYRIGHT_START ? `${COPYRIGHT_START}–${currentYear}` : `${COPYRIGHT_START}`;

  return (
    <FooterContainer $wide={isWide}>
      <FooterText>
        © {yearRange} TRIPLE FREEZER ®. All rights reserved.
      </FooterText>
      <RightSlot>
        <SocialLink
          href="https://www.instagram.com/thetriplefreezer/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Triple Freezer on Instagram"
        >
          <InstagramIcon />
        </SocialLink>
        <SocialLink
          href="https://www.youtube.com/@TRIPLEFREEZER"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Triple Freezer on YouTube"
        >
          <YouTubeIcon />
        </SocialLink>
      </RightSlot>
    </FooterContainer>
  );
};

export default Footer;